import { ProcessRelation, ReactFlowEdge, ReactFlowNode } from '@models/process-map.model';
import { Process } from '@models/process.model';
import { MarkerType } from 'reactflow';
import { CONTAINER_PADDING, EXPANDED_NODE_BUFFER, NODE_HORIZONTAL_SPACING, NODE_MARGIN } from './processMapConstants';

// Helper function to count all steps including substeps within SWITCH/REPEAT
const countAllSteps = (steps: any[]): number => {
  if (!steps) return 0;
  let count = 0;
  steps.forEach((step) => {
    count++; // Count the step itself
    // If it's a SWITCH/REPEAT step with substeps, count those too
    if (step.stepData?.stepsIds && typeof step.stepData.stepsIds === 'string') {
      const substepIds = step.stepData.stepsIds.split(',').filter((id: string) => id && id !== 'undefined');
      count += substepIds.length;
    }
  });
  return count;
};

export function drawRelated(
  related: ProcessRelation[] | undefined,
  identifier: string,
  allProcesses: Process[],
  originNodes: ReactFlowNode[],
) {
  if (!related || related.length === 0) {
    return {
      nodes: [],
      edges: [],
    };
  } else {
    // Group relations by origin
    const relationsByOrigin = related.reduce(
      (acc, relation) => {
        if (!acc[relation.origin]) {
          acc[relation.origin] = [];
        }
        acc[relation.origin].push(relation);
        return acc;
      },
      {} as Record<string, ProcessRelation[]>,
    );

    const nodes: ReactFlowNode[] = related
      .map((relation) => {
        const destinationProcess = allProcesses.find((process) => process.processId === relation.destination);
        if (!destinationProcess) return null;

        // Find the origin node
        const containerOriginNode = originNodes.find(
          (n) => n.id === originNodes.find((node) => node.id === `${identifier}-${relation.origin}`)?.parentNode,
        );
        const originNode = originNodes.find((node) => node.id === `${identifier}-${relation.origin}`);
        if (!originNode || !containerOriginNode) return null;

        // Get all relations for this origin
        const relationsForThisOrigin = relationsByOrigin[relation.origin];
        const relationIndex = relationsForThisOrigin.indexOf(relation);
        const VERTICAL_SPACING = 100;

        // Position calculation - relative to container since we'll set parentNode
        // Place related processes horizontally to the right, continuing the flow
        // Stack multiple relations vertically if there's more than one
        const position = {
          x: originNode.position.x + NODE_HORIZONTAL_SPACING,
          y: originNode.position.y + (relationIndex * VERTICAL_SPACING),
        };

        return {
          id: `${identifier}-related-${relation.destination}`,
          type: 'ProcessV2',
          position,
          parentNode: containerOriginNode.id,
          data: {
            originalY: position.y,
            processId: destinationProcess.processId,
            preLabel: destinationProcess.service?.area?.name + ' - ' + destinationProcess.service?.name,
            label: destinationProcess.name,
            color: destinationProcess.service?.color || '#F0F0F0',
            backgroundColor: destinationProcess.service?.color || '#F0F0F0',
            process: destinationProcess,
            steps: destinationProcess.steps,
            isExternal: true,
            parentNode: containerOriginNode.id,
            categoryId: destinationProcess.service?.serviceId + '---' + destinationProcess.category,
            type: 'related',
            status: destinationProcess?.status,
          },
        };
      })
      .filter((node) => node !== null) as ReactFlowNode[];

    const edges: ReactFlowEdge[] = related.map((relation) => {
      return {
        id: `${identifier}-related-edge-${relation.origin}-${relation.destination}`,
        source: `${identifier}-${relation.origin}`,
        target: `${identifier}-related-${relation.destination}`,
        type: 'smoothstep',
        // Let ReactFlow auto-calculate handles based on node positions for better connections
        sourceHandle: undefined,
        targetHandle: undefined,
        markerEnd: {
          type: MarkerType.Arrow,
          width: 30,
          height: 30,
        },
      };
    });

    // Here we need to check the height of the related nodes and adjust the height of the container if necessary

    const containerNode = originNodes.find((node) => node.id === 'container-' + identifier);

    if (!containerNode) return { nodes, edges };

    const contentHeight = Math.max(
      ...nodes
        .filter((node) => node.id !== 'container-' + identifier)
        .map((item) => {
          const height =
            item.type === 'ProcessV2' && item.data.expand && item.data.steps
              ? countAllSteps(item.data.steps) * 40 + EXPANDED_NODE_BUFFER
              : 0;
          return item.position.y - containerNode.position.y + height;
        }),
    );

    const totalHeight = contentHeight + (CONTAINER_PADDING + NODE_MARGIN);

    const currentHeight = containerNode.data.height || 0;

    // Update container dimensions to accommodate triggers and contained nodes
    containerNode.data.height = Math.max(totalHeight, currentHeight + CONTAINER_PADDING);
    containerNode.data.originalHeight = Math.max(totalHeight, currentHeight + CONTAINER_PADDING);

    return {
      nodes,
      edges,
    };
  }
}
